'use client'

import type { ReactNode } from 'react'
import {
  Ban,
  Car,
  Compass,
  Home,
  Sparkles,
  SunMedium,
  TreePine,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Header } from '@/components/landing/header'
import { Footer } from '@/components/landing/footer'
import {
  marketingCreamPanel,
  marketingDarkPanel,
  marketingDarkTextPrimary,
  marketingDarkTextSecondary,
  marketingGlassCard,
  marketingIconGradient,
  marketingLightTextPrimary,
  marketingLightTextSecondary,
  marketingRoundedSurface,
} from '@/components/landing/marketingV2Surfaces'
import { cn } from '@/lib/utils'
import { MARKETING_TRAVEL_PRODUCT } from '@/components/landing/marketingTravelCopy'

/** Section wrappers own vertical padding; column gap stays minimal */
const SECTION_GAP = 'gap-y-6 pb-24 pt-8 md:gap-y-8 md:pb-32 md:pt-10'

/** Strong card treatment — System anchor */
const conceptCardClass = cn(
  marketingGlassCard,
  'p-10 transition hover:-translate-y-0.5 hover:shadow-[0_28px_82px_-34px_rgba(255,127,164,0.3)] md:p-11'
)

const productTileClass = cn(marketingGlassCard, 'flex h-full flex-col gap-4 p-7 md:p-8')

const boundaryCardClass = cn(marketingGlassCard, 'flex gap-4 rounded-[26px] p-7 md:p-8')

const aboutCreamPanelClass = cn(marketingCreamPanel, 'px-6 py-16 sm:px-8 md:px-10 md:py-20 lg:px-14 lg:py-24')
const aboutPlainPanelClass = cn(marketingRoundedSurface, 'px-6 py-16 sm:px-8 md:px-10 md:py-20 lg:px-14 lg:py-24')
const aboutDarkPanelClass = cn(marketingDarkPanel, 'px-6 py-16 sm:px-8 md:px-10 md:py-20 lg:px-14 lg:py-24')
const aboutDarkHeadingClass = 'text-[#F6EDE4]'
const aboutDarkBodyClass = 'text-[#E6D7C3]'
const aboutDarkMutedClass = 'text-[#D7C6B4]'

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#07060A_0%,#18141e_24%,#FFF8EF_24%,#FFF8EF_100%)]">
      <Header />
      <main className="pt-20 md:pt-12">
        {/* Opening */}
        <section className={cn(marketingDarkPanel, 'mx-4 text-white sm:mx-6 lg:mx-8 xl:mx-auto xl:max-w-6xl')}>
          <div className="pointer-events-none absolute inset-0 opacity-60">
            <div className="absolute left-10 top-20 h-40 w-40 rounded-full bg-[#FF8A65]/24 blur-3xl" />
            <div className="absolute bottom-10 right-10 h-48 w-48 rounded-full bg-[#F6B443]/20 blur-3xl" />
          </div>
          <div className="container-mobile relative py-28 md:py-36 lg:py-44">
            <div className="mx-auto max-w-2xl space-y-8 text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#FDE68A]">
                About Wagamour
              </p>
              <h1 className={cn('font-heading text-4xl font-bold leading-[1.1] md:text-5xl lg:text-[3.35rem]', marketingDarkTextPrimary)}>
                The Pet Mobility Platform
              </h1>
              <p className={cn('text-lg leading-relaxed [text-wrap:pretty] md:text-xl md:leading-relaxed', marketingDarkTextPrimary)}>
                Wagamour helps you make the right call as you move through the
                world with your dog—and act on it.
              </p>
              <p className={cn('text-base font-medium leading-relaxed [text-wrap:pretty] md:text-lg md:leading-relaxed', marketingDarkTextSecondary)}>
                It shows you where to go, what to do, and how to take the next
                step—right when it matters.
              </p>
            </div>
          </div>
        </section>

        <div
          className={`mx-auto flex max-w-6xl flex-col px-6 sm:px-8 lg:px-10 ${SECTION_GAP}`}
        >
          {/* Why — narrative anchor, rounded V2 surface */}
          <div className={aboutDarkPanelClass}>
            <NarrativeSection id="about-why" title="Why Wagamour exists" tone="dark">
              <p>Traveling with a dog changes everything.</p>
              <p>
                A simple drive turns into planning stops, checking conditions, and
                constantly asking: is this place right for them?
              </p>
              <p>
                Whether you&apos;re going across town or on a longer trip, you
                rarely know what you&apos;ll actually find. Which park actually fits
                your dog. Where to stop. Whether the ground is too hot. What to do
                if something goes wrong.
              </p>
              <p>
                You open maps, search, guess, and piece things together as you go.
              </p>
              <p>
                Wagamour was built from that experience—turning uncertain moments
                into clear decisions, and decisions into action.
              </p>
            </NarrativeSection>
          </div>

          {/* Problem — tinted band, contrast beat */}
          <section
            aria-labelledby="about-problem"
            className={aboutDarkPanelClass}
          >
            <div className="mx-auto max-w-3xl">
              <h2
                id="about-problem"
                className={cn('text-center font-heading text-xl font-bold md:text-2xl', aboutDarkHeadingClass)}
              >
                The problem
              </h2>
              <p className={cn('mt-5 text-center text-base leading-relaxed md:text-lg', aboutDarkBodyClass)}>
                It&apos;s not one big decision—it&apos;s a series of small ones,
                constantly changing as the day unfolds.
              </p>
              <p className={cn('mt-3 text-center text-base leading-relaxed md:text-lg', aboutDarkBodyClass)}>
                But most tools still behave like feeds or directories—they show
                information, but don&apos;t help you decide what to do.
              </p>
              <ul className={cn('mx-auto mt-6 max-w-md list-none space-y-2.5 text-center text-base md:text-lg', aboutDarkMutedClass)}>
                <li>Is this pavement too hot?</li>
                <li>Is now the right time for a longer walk?</li>
                <li>Which park actually fits today?</li>
              </ul>
              <p className={cn('mx-auto mt-6 max-w-md text-center text-base md:text-lg', aboutDarkBodyClass)}>
                Should you go now, wait, or change plans?
              </p>
              <p className={cn('mt-8 text-center text-base font-medium md:text-lg', aboutDarkHeadingClass)}>
                Most tools don&apos;t reflect the moment you&apos;re in.
              </p>
            </div>
          </section>

          {/* System — conceptual core */}
          <section
            aria-labelledby="about-system"
            className={cn(aboutPlainPanelClass, 'scroll-mt-28')}
          >
            <div className="mx-auto max-w-5xl">
              <div className="mb-14 text-center">
                <h2
                  id="about-system"
                  className={cn('font-heading text-2xl font-bold tracking-tight md:text-3xl', marketingLightTextPrimary)}
                >
                  The system
                </h2>
                <p className={cn('mx-auto mt-4 max-w-2xl text-base leading-relaxed md:text-lg', marketingLightTextSecondary)}>
                  Wagamour is built on two ideas: understanding comfort in the
                  moment, and helping you move from decision to action.
                </p>
              </div>
              <div className="grid gap-10 md:grid-cols-2 lg:gap-12">
                <Card className={conceptCardClass}>
                  <div className={cn('mb-6 flex h-12 w-12 items-center justify-center rounded-2xl', marketingIconGradient)}>
                    <SunMedium className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className={cn('font-heading text-xl font-bold md:text-2xl', marketingLightTextPrimary)}>
                    Pet Comfort Intelligence™
                  </h3>
                  <p className={cn('mt-4 text-base leading-relaxed', marketingLightTextSecondary)}>
                    Pet Comfort Intelligence™ provides a clear, grounded read on
                    conditions—air, surfaces, and how your dog changes what
                    &quot;safe&quot; means. It surfaces signals clearly and adapts
                    based on your dog.
                  </p>
                </Card>
                <Card className={conceptCardClass}>
                  <div className={cn('mb-6 flex h-12 w-12 items-center justify-center rounded-2xl', marketingIconGradient)}>
                    <Compass className="h-6 w-6" aria-hidden />
                  </div>
                  <h3 className={cn('font-heading text-xl font-bold md:text-2xl', marketingLightTextPrimary)}>
                    Decision → action
                  </h3>
                  <p className={cn('mt-4 text-base leading-relaxed', marketingLightTextSecondary)}>
                    From there, Wagamour helps you decide what to do next—and,
                    when it makes sense, take action.
                  </p>
                  <p className={cn('mt-5 text-base leading-relaxed', marketingLightTextSecondary)}>
                    You can decide whether to go now or wait, find a place that
                    fits today, or take the next step—without jumping between
                    tools. Over time, it becomes a loop: decision, action,
                    outcome, and learning.
                  </p>
                </Card>
              </div>
            </div>
          </section>

          {/* Product — structured, airy grid */}
          <section
            aria-labelledby="about-product"
            className={aboutDarkPanelClass}
          >
            <div className="mx-auto max-w-5xl">
              <div className="mx-auto max-w-2xl text-center">
                <h2
                  id="about-product"
                  className={cn('font-heading text-2xl font-bold tracking-tight md:text-3xl', aboutDarkHeadingClass)}
                >
                  The product
                </h2>
                <p className={cn('mt-5 text-base leading-relaxed md:text-lg', aboutDarkBodyClass)}>
                  Wagamour follows how your day actually unfolds with your dog.
                </p>
                <p className={cn('mt-10 font-heading text-lg font-semibold tracking-wide md:text-xl', aboutDarkHeadingClass)}>
                  Home → Play → Parks → Picks → Travel
                </p>
              </div>
              <div className="mt-16 grid gap-7 sm:grid-cols-2 lg:gap-8 lg:gap-y-8">
                <ProductSurfaceCard
                  icon={Home}
                  title="Home"
                  body="Gives you a quick understanding of your current conditions and whether it's a good time to head out."
                />
                <ProductSurfaceCard
                  icon={Users}
                  title="Play"
                  body="Helps you connect with nearby dogs when it actually makes sense, based on proximity and compatibility."
                />
                <ProductSurfaceCard
                  icon={TreePine}
                  title="Parks"
                  body="Helps you discover places that fit the moment, not just what's nearby."
                />
                <ProductSurfaceCard
                  icon={Sparkles}
                  title="Picks"
                  body="Helps you move from decision to action—choosing where to go, finding what you need, or taking the next step in real time."
                />
                <ProductSurfaceCard
                  icon={Car}
                  title="Travel"
                  status="Now testing"
                  className="sm:col-span-2"
                  body="Helps you prepare, understand conditions on the way, and arrive with more context for your dog."
                />
              </div>
              <p className={cn('mx-auto mt-10 max-w-xl text-center text-sm font-medium leading-relaxed', aboutDarkMutedClass)}>
                {MARKETING_TRAVEL_PRODUCT.disclosure}
              </p>
            </div>
          </section>

          {/* Beyond — narrative opens */}
          <section
            aria-labelledby="about-beyond"
            className={aboutPlainPanelClass}
          >
            <div className="mx-auto max-w-3xl">
              <h2
                id="about-beyond"
                className={cn('text-center font-heading text-2xl font-bold tracking-tight md:text-3xl', marketingLightTextPrimary)}
              >
                Beyond today
              </h2>
              <div className={cn('mt-10 space-y-8 text-center text-base leading-[1.75] md:text-lg md:leading-relaxed', marketingLightTextSecondary)}>
                <p className="[text-wrap:pretty]">
                  The day doesn&apos;t start when you get in the car. It begins at
                  home, continues through how you move, and carries into
                  wherever you arrive.
                </p>
                <p className="[text-wrap:pretty]">
                  That entire journey is where products need to meet people.
                </p>
                <p className="[text-wrap:pretty]">
                  We start with dogs because the feedback is immediate and real.
                  But the same pattern applies whether you&apos;re
                  crossing town or traveling longer distances—what to trust, what
                  to skip, and what to do next.
                </p>
              </div>
              <p className={cn('mt-10 text-center text-base font-medium md:text-lg', marketingLightTextPrimary)}>
                Over time, this extends across the full journey:
              </p>
              <div className={cn('mx-auto mt-6 max-w-xl space-y-2 text-center text-base md:text-lg', marketingLightTextSecondary)}>
                <p>Before you leave</p>
                <p>During the trip</p>
                <p>After you arrive</p>
              </div>
              <p className={cn('mx-auto mt-10 max-w-3xl text-center text-base leading-relaxed md:text-lg', marketingLightTextSecondary)}>
                Services and providers—walkers, sitters, groomers—belong in that
                same flow, but only when they feel coordinated and reliable.
              </p>
            </div>
          </section>

          {/* What we&apos;re not */}
          <section
            aria-labelledby="about-not"
            className={aboutPlainPanelClass}
          >
            <div className="mx-auto max-w-4xl">
              <div className="text-center">
                <h2
                  id="about-not"
                  className={cn('font-heading text-2xl font-bold tracking-tight md:text-3xl', marketingLightTextPrimary)}
                >
                  What we&apos;re not
                </h2>
                <p className={cn('mx-auto mt-4 max-w-lg text-base', marketingLightTextSecondary)}>
                  Clarity also comes from what we choose not to be.
                </p>
              </div>
              <div className="mt-12 flex flex-col gap-8 md:gap-10">
                <Card className={boundaryCardClass}>
                  <Ban
                    className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]"
                    aria-hidden
                  />
                  <p className={cn('text-sm leading-relaxed md:text-base', marketingLightTextSecondary)}>
                    Wagamour is not an open-ended Q&amp;A product. It&apos;s
                    structured around real decisions, not endless prompts.
                  </p>
                </Card>
                <Card className={boundaryCardClass}>
                  <Ban
                    className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]"
                    aria-hidden
                  />
                  <p className={cn('text-sm leading-relaxed md:text-base', marketingLightTextSecondary)}>
                    It&apos;s not a generic directory. Discovery is tied to movement,
                    conditions, and context—not every option within a radius.
                  </p>
                </Card>
                <Card className={boundaryCardClass}>
                  <Ban
                    className="mt-0.5 h-5 w-5 shrink-0 text-[#C2410C]"
                    aria-hidden
                  />
                  <p className={cn('text-sm leading-relaxed md:text-base', marketingLightTextSecondary)}>
                    And it&apos;s not one-size-fits-all advice. Place, weather, and
                    your dog shape the answer, not a template.
                  </p>
                </Card>
              </div>
            </div>
          </section>

          <ClosingSection />

        </div>
      </main>
      <Footer />
    </div>
  )
}

function NarrativeSection({
  id,
  title,
  children,
  tone = 'light',
}: {
  id: string
  title: string
  children: ReactNode
  tone?: 'light' | 'dark'
}) {
  const headingClass = tone === 'dark' ? aboutDarkHeadingClass : marketingLightTextPrimary
  const bodyClass = tone === 'dark' ? aboutDarkBodyClass : marketingLightTextSecondary

  return (
    <section aria-labelledby={id} className="mx-auto max-w-3xl text-center">
      <h2
        id={id}
        className={cn('font-heading text-2xl font-bold tracking-tight md:text-3xl', headingClass)}
      >
        {title}
      </h2>
      <div className={cn('mt-10 space-y-6 text-center text-base leading-[1.75] md:text-lg md:leading-relaxed [&>p]:[text-wrap:pretty]', bodyClass)}>
        {children}
      </div>
    </section>
  )
}

function ClosingSection() {
  return (
    <section
      aria-labelledby="about-closing"
      className={cn(marketingRoundedSurface, 'mx-auto max-w-2xl px-6 py-12 text-center md:px-10 md:py-16')}
    >
      <div className="mx-auto max-w-lg">
        <h2
          id="about-closing"
          className={cn('font-heading text-xl font-semibold leading-snug md:text-2xl md:leading-snug', marketingLightTextPrimary)}
        >
          Decisions should match the moment—and the dog you&apos;re with.
        </h2>
        <p className={cn('mt-8 text-base leading-relaxed md:text-lg md:leading-relaxed', marketingLightTextSecondary)}>
          Wagamour is built to help you make better decisions in real moments—and,
          when it matters, follow through with action that actually fits your dog,
          your surroundings, and your day.
        </p>
      </div>
    </section>
  )
}

function ProductSurfaceCard({
  icon: Icon,
  title,
  body,
  status,
  className,
}: {
  icon: LucideIcon
  title: string
  body: string
  status?: string
  className?: string
}) {
  return (
    <Card className={cn(productTileClass, className)}>
      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F6B443]/18 to-[#FF7FA4]/14 text-[#C2410C]">
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h3 className={cn('font-heading text-lg font-bold', marketingLightTextPrimary)}>{title}</h3>
          {status ? (
            <span className="rounded-full border border-[#E8CFAE] bg-[#FFF6E8] px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[#9A5B1A]">
              {status}
            </span>
          ) : null}
        </div>
        <p className={cn('mt-2 text-sm leading-relaxed md:text-base', marketingLightTextSecondary)}>
          {body}
        </p>
      </div>
    </Card>
  )
}
